import { useMutateExportCyclesSummary } from 'modules/pond/api/mutation'
import { ExportFormat } from 'modules/pond/constants'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { Button, Divider, Paper, Stack, Typography } from '@mui/material'

import { downloadFile, getProgress } from 'utils/download'

import ExportFormatMenu from './ExportFormatMenu'

interface CycleExportBarProps {
  /** Ids of the ticked cycles, in the order they appear in the table. */
  selectedCycleIds: string[]
}

/**
 * Floating bar shown while cycles are being picked: how many are ticked, and the
 * button that exports them. All the selected cycles come back as a single file, so
 * one request per export regardless of how many are ticked.
 */
const CycleExportBar = ({ selectedCycleIds }: CycleExportBarProps) => {
  const { t } = useTranslation(['pond', 'common'])
  const { enqueueSnackbar } = useSnackbar()
  const exportCyclesSummary = useMutateExportCyclesSummary()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [downloadingFormat, setDownloadingFormat] = useState<ExportFormat | null>(null)
  const [progress, setProgress] = useState(0)

  const hasSelection = selectedCycleIds.length > 0

  const onDownload = async (format: ExportFormat) => {
    setDownloadingFormat(format)
    setProgress(0)

    try {
      const response = await exportCyclesSummary.mutateAsync({
        query: {
          cycleIds: selectedCycleIds.join(','),
          format,
        },
        progressCb: (progressEvent) => setProgress(getProgress(progressEvent)),
      })
      downloadFile(response, `summary.${format}`)
      setAnchorEl(null)
    } catch (error) {
      enqueueSnackbar(t('pond:export.fail'), { variant: 'error' })
    }

    setDownloadingFormat(null)
    setProgress(0)
  }

  return (
    <Stack alignItems="center" sx={{ mt: 2 }}>
      <Paper
        elevation={0}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          boxShadow: (theme) => theme.customShadows?.z8,
          overflow: 'hidden',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="subtitle1" color="text.primary">
            {selectedCycleIds.length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pond:selected.cycles')}
          </Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Button
          variant="contained"
          color="primary"
          disabled={!hasSelection || downloadingFormat !== null}
          startIcon={<FileUploadOutlinedIcon />}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            m: 1,
            borderRadius: 1,
            textTransform: 'none',
            boxShadow: 'none',
          }}
        >
          {downloadingFormat ? `${progress}%` : t('pond:export.data')}
        </Button>
      </Paper>

      <ExportFormatMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onSelect={onDownload}
        downloadingFormat={downloadingFormat}
      />
    </Stack>
  )
}

export default CycleExportBar
